import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { HilCountdown } from '@bizfirst/hil-ui';
import type { HilChatWindowSettings } from '../settings/HilChatWindowSettings';

function resolveAttachTarget(attachTo: string | HTMLElement | undefined): HTMLElement | null {
  if (!attachTo) return null;
  if (typeof attachTo !== 'string') return attachTo;
  return document.querySelector(attachTo);
}

interface HilChatWindowChromeProps {
  settings: HilChatWindowSettings;
  title: string | null;
  deadline: string | null;
  onClose?: () => void;
  children: React.ReactNode;
}

/**
 * Implements overlay / docked / inline placement per settings.mount.mode, and the
 * window-chrome icon row per settings.windowControls. fullscreen/dock/minimize have
 * no committed behavior in Phase 1 beyond visibility — not over-designed ahead of a
 * real consumer needing them.
 */
export function HilChatWindowChrome({ settings, title, deadline, onClose, children }: HilChatWindowChromeProps): React.JSX.Element | null {
  const mode = settings.mount?.mode ?? 'overlay';
  const controls = settings.windowControls ?? {};
  const showHeader = settings.visibility?.header !== false;
  const attachTo = settings.mount?.attachTo;
  const [dockTarget, setDockTarget] = useState<HTMLElement | null>(() => resolveAttachTarget(attachTo));

  useEffect(() => {
    if (mode !== 'docked') return;
    setDockTarget(resolveAttachTarget(attachTo));
  }, [mode, attachTo]);

  const panel = (
    <div className="hil-chat-panel">
      {showHeader && (
        <div className="hil-chat-panel__header">
          <h2 className="hil-chat-panel__title">{title ?? 'Chat'}</h2>
          {deadline && <HilCountdown expiresAt={deadline} />}
          <div className="hil-chat-panel__controls">
            {controls.fullscreen && <button type="button" className="hil-chat-panel__icon" aria-label="Fullscreen" disabled />}
            {controls.dock && <button type="button" className="hil-chat-panel__icon" aria-label="Dock" disabled />}
            {controls.minimize && <button type="button" className="hil-chat-panel__icon" aria-label="Minimize" disabled />}
            {controls.close && (
              <button type="button" className="hil-chat-panel__icon" aria-label="Close" onClick={onClose} disabled={!onClose}>
                ×
              </button>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  );

  if (mode === 'overlay') {
    return <div className="hil-chat-overlay-backdrop">{panel}</div>;
  }

  if (mode === 'docked') {
    // settings.mount.attachTo (CSS selector or element) is required for 'docked' — without
    // a resolvable target there is nowhere to portal into, so render nothing rather than
    // silently falling back to 'inline' placement the app didn't ask for.
    return dockTarget ? createPortal(panel, dockTarget) : null;
  }

  // 'inline' renders in normal document flow at the call site.
  return panel;
}
